class BirdeeAnimation extends HTMLElement {
  #lottie = null;
  #animation = null;
  #isMobile = null;
  #phase = null;
  #resizeTimeout = null;
  #animationStarted = false;
  #onScroll = null;

  connectedCallback() {
    const container = this.querySelector('[data-birdee-container]') || this;
    if (container.dataset.loaded) return;
    container.dataset.loaded = 'true';
    this.container = container;

    import('https://cdn.jsdelivr.net/npm/lottie-web@5.12.2/+esm').then(({ default: lottie }) => {
      this.#lottie = lottie;
      this.#initPlayback();
      window.addEventListener('resize', this.#onResize);
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    this.#removeScrollListener();
    clearTimeout(this.#resizeTimeout);
    this.#destroyAnimation();
  }

  #onResize = () => {
    clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = setTimeout(() => {
      const isMobile = window.innerWidth < 1024;
      if (isMobile === this.#isMobile) return;
      this.#animationStarted = false;
      this.#removeScrollListener();
      this.#destroyAnimation();
      this.#initPlayback();
    }, 150);
  };

  #isMobileViewport() {
    return window.innerWidth < 1024;
  }

  #getUrls() {
    this.#isMobile = this.#isMobileViewport();
    return {
      fly: this.#isMobile
        ? this.dataset.animationMobileFlyUrl
        : this.dataset.animationDesktopFlyUrl,
      idle: this.#isMobile
        ? this.dataset.animationMobileIdleUrl
        : this.dataset.animationDesktopIdleUrl,
    };
  }

  #destroyAnimation() {
    if (!this.#animation) return;
    this.#animation.removeEventListener('complete', this.#onFlyComplete);
    this.#animation.destroy();
    this.#animation = null;
  }

  #initPlayback() {
    if (!this.#lottie) return;

    if (this.#isMobileViewport()) {
      this.#setupMobileScrollTrigger();
      return;
    }

    this.#showContainer();
    this.#start();
  }

  #hideContainer() {
    this.container.style.opacity = '0';
    this.container.style.transition = 'opacity 0.6s ease';
  }

  #showContainer() {
    this.container.style.opacity = '1';
    this.container.style.transition = 'opacity 0.6s ease';
  }

  #removeScrollListener() {
    if (!this.#onScroll) return;
    window.removeEventListener('scroll', this.#onScroll);
    this.#onScroll = null;
  }

  #setupMobileScrollTrigger() {
    const addToCart = document.getElementById('AddToCart');
    if (!addToCart) {
      this.#showContainer();
      this.#start();
      return;
    }

    this.#hideContainer();

    this.#onScroll = () => {
      const { bottom } = addToCart.getBoundingClientRect();
      const isPastButton = bottom < 0;

      if (isPastButton) {
        if (!this.#animationStarted) {
          this.#animationStarted = true;
          this.#start();
        }
        this.#showContainer();
      } else {
        this.#hideContainer();
      }
    };

    this.#onScroll();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #start() {
    if (!this.#lottie) return;

    const { fly, idle } = this.#getUrls();
    if (!fly || !idle) return;

    this.#destroyAnimation();
    this.#phase = 'fly';
    this.#animation = this.#lottie.loadAnimation({
      container: this.container,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      path: fly,
    });
    this.#animation.addEventListener('complete', this.#onFlyComplete);
    this._idleUrl = idle;
  }

  #onFlyComplete = () => {
    if (this.#phase !== 'fly' || !this.#lottie) return;

    this.#destroyAnimation();
    this.#phase = 'idle';
    this.#animation = this.#lottie.loadAnimation({
      container: this.container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: this._idleUrl,
    });
  };
}

customElements.define('birdee-animation', BirdeeAnimation);
